import React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Box, Avatar, IconButton, Tooltip, Typography } from '@mui/material';
import { AdminPanelSettings, ContentCopy, Refresh, PictureAsPdf } from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';

import { chatMessagesState } from '../../state/chat/ChatMessagesState';
import { loadingChatMessageState } from '../../state/chat/LoadingChatMessageState';
import { suggestionsState } from '../../state/chat/SuggestionsState';
import { chatIdState } from '../../state/chat/ChatIdState';
import { chatbotConfigState } from '../../state/config/ChatbotConfigState';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { useAppTranslation } from '../../hooks/useAppTranslation';

export function ChatHeader({ isMobile }) {
    const [chatMessages, setChatMessages] = useAtom(chatMessagesState);
    const [, setSuggestions] = useAtom(suggestionsState);
    const isLoading = useAtomValue(loadingChatMessageState);
    const [, setChatId] = useAtom(chatIdState);
    const config = useAtomValue(chatbotConfigState);
    const { t, lang } = useAppTranslation();
    const navigate = useNavigate();

    const primaryColor = config.primaryColor || '#172983';
    const chatbotName = config.chatbotName || t.defaultChatbotName;
    const canExport = !isLoading && chatMessages.length > 0;
    const canCopy = !isLoading && chatMessages.length > 0;

    function onRefresh() {
        if (isLoading) return;
        const firstMessage = chatMessages[0];
        setChatMessages(firstMessage ? [firstMessage] : []);
        setSuggestions([]);
        setChatId(uuidv4());
    }

    function copyMessagesToClipboard() {
        const messagesText = chatMessages
            .map((msg) => `${msg.sender === 'user' ? t.userLabel : chatbotName}: ${msg.message}`)
            .join('\n\n');

        navigator.clipboard
            .writeText(messagesText)
            .then(() => showToastSuccess(t.copiedToClipboard))
            .catch((err) => showToastError(t.copyFailedWithReason.replace('{error}', String(err))));
    }

    function downloadPDF() {
        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();

            doc.setFontSize(18);
            doc.text(t.chatTranscriptTitle.replace('{name}', chatbotName), 12, 14);
            doc.setFontSize(10);
            doc.text(t.exportedAt.replace('{date}', new Date().toLocaleString(String(lang || '').toLowerCase().startsWith('en') ? 'en-US' : 'de-DE')), 12, 22);

            let y = 34;

            chatMessages.forEach((msg) => {
                const sender = msg.sender === 'user' ? t.userLabel : chatbotName;
                const safeText = msg.message || '';
                const splitText = doc.splitTextToSize(safeText, pageWidth - 24);

                if (y > 270) {
                    doc.addPage();
                    y = 14;
                }

                doc.setFont('helvetica', 'bold');
                doc.text(`${sender}:`, 12, y);
                y += 6;

                doc.setFont('helvetica', 'normal');
                doc.text(splitText, 12, y);
                y += splitText.length * 5 + 6;
            });

            doc.save('chat_transcript.pdf');
            showToastSuccess(t.chatHeaderPdfDownloaded);
        } catch (error) {
            console.error(error);
            showToastError(t.chatHeaderPdfCreateFailed);
        }
    }

    function handleAdminClick() {
        navigate('admin');
    }

    const actionButtonSx = {
        width: 34,
        height: 34,
        color: 'white',
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        border: '1px solid rgba(255, 255, 255, 0.14)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.22)',
            transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
            outline: '2px solid rgba(255,255,255,0.85)',
            outlineOffset: 2,
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.45)',
            backgroundColor: 'rgba(255,255,255,0.08)',
        },
    };

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, rgba(13, 25, 87, 0.96) 100%)`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.15,
                px: isMobile ? 1.25 : 1.75,
                py: isMobile ? 1 : 1.1,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 8px 22px rgba(10, 20, 60, 0.16)',
            }}
        >
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Avatar
                    src={config.logoUrl}
                    alt={t.chatHeaderAvatarAlt}
                    sx={{
                        width: 42,
                        height: 42,
                        border: '2px solid rgba(255,255,255,0.9)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 1,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: '#22c55e',
                        border: '2px solid white',
                    }}
                />
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant='h6'
                    sx={{
                        color: 'white',
                        fontSize: isMobile ? '0.98rem' : '1rem',
                        fontWeight: 700,
                        lineHeight: 1.12,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: isMobile ? '42vw' : 'min(32vw, 320px)',
                    }}
                >
                    {chatbotName}
                </Typography>
                <Typography
                    variant='caption'
                    sx={{
                        color: 'rgba(255,255,255,0.76)',
                        letterSpacing: '0.02em',
                        fontSize: isMobile ? '0.68rem' : '0.72rem',
                    }}
                >
                    {isLoading ? t.thinkingEllipsis : t.chatHeaderReady}
                </Typography>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    p: 0.4,
                    borderRadius: 99,
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.10)',
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Tooltip title={t.chatHeaderSavePdf}>
                    <span>
                        <IconButton onClick={downloadPDF} disabled={!canExport} sx={actionButtonSx} aria-label={t.chatHeaderSavePdf}>
                            <PictureAsPdf fontSize='small' />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t.chatHeaderOpenAdmin}>
                    <IconButton onClick={handleAdminClick} sx={actionButtonSx} aria-label={t.chatHeaderOpenAdmin}>
                        <AdminPanelSettings fontSize='small' />
                    </IconButton>
                </Tooltip>

                <Tooltip title={t.chatHeaderCopyChat}>
                    <span>
                        <IconButton onClick={copyMessagesToClipboard} disabled={!canCopy} sx={actionButtonSx} aria-label={t.chatHeaderCopyChat}>
                            <ContentCopy fontSize='small' />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title={t.chatHeaderNewChat}>
                    <IconButton onClick={onRefresh} sx={actionButtonSx} aria-label={t.chatHeaderNewChat}>
                        <Refresh fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}
